import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {contextMenu} from 'react-contexify';
import isEqual from 'react-fast-compare';

import NiceModal from '@ebay/nice-modal-react';
import classNames from 'classnames';
import {action, runInAction} from 'mobx';
import {Observer} from 'mobx-react';

import type {EditionApiModel} from '@yourcoach/shared/api/program';
import type {
  PaymentPlan,
  ProgramPaymentOption,
  ProgramType,
} from '@yourcoach/shared/utils/entity/program';

import {delay} from '@yourcoach/async-utils';
import {i18n, useI18n} from '@yourcoach/i18n';
import {type FileApiModel, fileStore} from '@yourcoach/shared/api/media/file';
import {currentUserStore} from '@yourcoach/shared/api/user';
import {apiRequest} from '@yourcoach/shared/api/utils/apiRequest';
import ArrowNextIcon from '@yourcoach/shared/assets/icons/arrow-next.svg';
import CheckIcon from '@yourcoach/shared/assets/icons/check.svg';
import CloneProgramIcon from '@yourcoach/shared/assets/icons/clone-program.svg';
import DocumentIcon from '@yourcoach/shared/assets/icons/secondary/Paper.svg';
import ProgramGroupIcon from '@yourcoach/shared/assets/program-group@3x.png';
import ProgramIndividualIcon from '@yourcoach/shared/assets/program-individual@3x.png';
import {connectStripeConfirm} from '@yourcoach/shared/modules/program/cru/connectStripeConfirm';
import {Image} from '@yourcoach/shared/uikit/Image';
import {Toggle} from '@yourcoach/shared/uikit/Toggle';
import {
  getFileExtension,
  getFileName,
  getFileSrc,
  SUPPORTED_FORMATS,
} from '@yourcoach/shared/utils/entity/file';
import {showAlert} from '@yourcoach/shared/utils/entity/file/validateVideoSizeAndDuration/showAlert';
import {
  CLIENT_CONFERENCE_DURATIONS,
  createPaymentPlan,
  DEFAULT_CLIENT_CONFERENCES_COUNT,
  DEFAULT_DURATION,
  DEFAULT_GROUP_SIZE,
  DefaultPaymentPlan,
  EVERGREEN_PROGRAM_DURATION,
  getMonthlyPaymentPerUnitString,
  isEditionEvergreen,
  isMonthlyPaymentAvailable,
  MAX_GROUP_SIZE,
  MAX_PRICE,
  MIN_GROUP_SIZE,
  MIN_PRICE,
  parseDuration,
  parsePaymentPlan,
  ProgramPaymentOptionEnum,
  ProgramPaymentTypeEnum,
  ProgramTypeEnum,
} from '@yourcoach/shared/utils/entity/program';
import {emitter} from '@yourcoach/shared/utils/eventEmitter';
import {logger} from '@yourcoach/shared/utils/logger';
import {
  Currency,
  extractCommission,
  getPriceString,
} from '@yourcoach/shared/utils/money';
import {TestID} from '@yourcoach/shared/utils/test-utils/data-test-id';
import {validateFields} from '@yourcoach/shared/utils/validation';
import {createHtmlInputField} from '@yourcoach/shared/utils/validation/createHtmlInputField';

import type {ILink, TListDataItem} from '@src/models/library';

import Button from '@src/components/Button';
import ModalAnimateWin from '@src/components/ModalAnimateWin/ModalAnimateWin';
import Switch from '@src/components/Switch';
import TextField from '@src/components/TextField';
import useIsVisible from '@src/hooks/useIsVisible';
import AddFileModal from '@src/modules/library/AddFileModal';
import LibraryState from '@src/modules/MyLibrary/context/LibraryState';
import {connectStripe} from '@src/pages/ConnectStripe';
import {ContextMenu} from '@src/v2/components/ContextMenu';
import {DocumentViewerModal} from '@src/v2/modules/document-viewer-modal';

import type {ProgramT} from '.';
import type {TabProps, TabRef} from './useTabs';

import DurationModal from './DurationModal';
import AddFile from './legacy/AddFile';
import PaymentPlanModal from './PaymentPlanModal';
import PrivateDataRequestModal from './PrivateDataRequestModal';

import styles from './Details.module.css';
import commonStyles from './styles.module.css';

const CONTRACT_CONTEXT_MENU_ID = 'contract-context-menu';

export const PROGRAM_CONTRACT_UPLOADING_KEY = 'program_contract';

export const PROGRAM_TYPE_CHANGE_EVENT = 'program_type_change';

const CRUProgramDetails = forwardRef<TabRef, TabProps>(
  (
    {cloneProgram, isOfferedProgram, program, shouldCloneProgram, userIsOwner},
    ref,
  ) => {
    useImperativeHandle(ref, () => ({
      getData,
    }));

    const {t} = useI18n();
    const [type, setType] = useState(ProgramTypeEnum.GROUP);
    const [duration, setDuration] = useState(DEFAULT_DURATION);
    const [isEvergreen, setIsEvergreen] = useState(false);
    const [sentInvitesCount, setSentInvitesCount] = useState<null | number>(
      null,
    );
    const [currency, setCurrency] = useState<Currency>(
      Currency[DefaultPaymentPlan.currency],
    );
    const [paymentOption, setPaymentOption] = useState(
      ProgramPaymentOptionEnum.FREE,
    );
    const [paymentPlan, setPaymentPlan] = useState(DefaultPaymentPlan);
    const [paymentProcessing, setPaymentProcessing] =
      useState<PaymentPlan['processing']>('internal');
    const [contract, setContract] = useState<File | FileApiModel>();
    const [privateDataRequest, setPrivateDataRequest] = useState<
      EditionApiModel['private_data_request']
    >([]);

    const [clientConferenceDuration, setClientConferenceDuration] = useState(
      CLIENT_CONFERENCE_DURATIONS[0],
    );

    const groupSize = useRef(DEFAULT_GROUP_SIZE);
    const price = useRef(0);
    const fetchInvitesCountDelay = useRef(delay(0));

    const parsedDuration = useMemo(() => parseDuration(duration), [duration]);
    const isDurationEditable = useMemo(() => {
      if (!program || !program._id) {
        return true;
      }

      if (
        sentInvitesCount === 0 &&
        program.counters &&
        program.counters.memberships
      ) {
        return Object.keys(program.counters.memberships).every(
          key => program.counters.memberships[key] === 0,
        );
      }

      return false;
    }, [program, sentInvitesCount]);

    const isEvergreenEditable = useMemo(() => {
      if (!program || !program._id) {
        return true;
      }

      return program.status === 'draft';
    }, [program]);

    const parsedPaymentPlan = useMemo(
      () => parsePaymentPlan(paymentPlan),
      [paymentPlan],
    );
    const commission = useMemo(
      () =>
        extractCommission(
          parsedPaymentPlan.monthlyPayment || parsedPaymentPlan.total,
        ),
      [parsedPaymentPlan.monthlyPayment, parsedPaymentPlan.total],
    );

    const monthlyPaymentIsAvailableMemo = useMemo(
      () => isMonthlyPaymentAvailable(parsedPaymentPlan.total, duration),
      [duration, parsedPaymentPlan.total],
    );

    const internalProcessingIsAvailable = useMemo(() => {
      if (!program) {
        return true;
      } else if (program.status === 'active') {
        return program.coach_ids.length > 1
          ? program.expanded_coaches.every(coach => coach.is_provider)
          : true;
      }

      return true;
    }, [program]);

    const [durationModalIsOpen, showDurationModal, hideDurationModal] =
      useIsVisible();

    const [paymentPlanModalIsOpen, showPaymentPlanModal, hidePaymentPlanModal] =
      useIsVisible();

    const [
      privateDataRequestModalIsOpen,
      showPrivateDataRequestModal,
      hidePrivateDataRequestModal,
    ] = useIsVisible();

    const [addContractModalIsOpen, showAddContractModal, hideAddContractModal] =
      useIsVisible();

    const [
      addContractFromLibraryModalIsOpen,
      showAddContractFromLibraryModal,
      hideAddContractFromLibraryModal,
    ] = useIsVisible();

    const fields = useRef({
      clientConferencesCount: createHtmlInputField('clientConferencesCount', {
        defaultValue: `${DEFAULT_CLIENT_CONFERENCES_COUNT}`,
        validationRule: {
          optional: true,
          type: 'string',
        },
      }),
      groupSize: createHtmlInputField('group_size', {
        customValidate: action(() => {
          if (fields.groupSize.validationRule!.optional) {
            fields.groupSize.error = null;

            return true;
          }

          const field = fields.groupSize;

          let isValid = true;

          field.error = null;

          if (
            (groupSize.current > 0 && groupSize.current < MIN_GROUP_SIZE) ||
            !groupSize.current
          ) {
            field.error = t(
              'shared:CRUProgramDetails.group_size_min_error_message',
              {
                value: MIN_GROUP_SIZE,
              },
            );
            isValid = false;
          } else if (groupSize.current > MAX_GROUP_SIZE) {
            field.error = t(
              'shared:CRUProgramDetails.group_size_max_error_message',
              {
                value: MAX_GROUP_SIZE,
              },
            );
            isValid = false;
          }

          field.isValid = isValid;

          return isValid;
        }),
        defaultValue: `${DEFAULT_GROUP_SIZE}`,
        validationRule: {
          type: 'string',
        },
      }),
      price: createHtmlInputField('price', {
        customValidate: action(() => {
          if (fields.price.validationRule!.optional) {
            fields.price.error = null;

            return true;
          }

          const field = fields.price;

          let isValid = true;

          field.error = null;

          if (
            (price.current > 0 && price.current < MIN_PRICE) ||
            !price.current
          ) {
            field.error = t(
              'shared:CRUProgramDetails.min_price_error_message',
              {
                value: getPriceString({
                  currency: currency,
                  price: MIN_PRICE,
                }),
              },
            );
            isValid = false;
          } else if (price.current > MAX_PRICE) {
            field.error = t(
              'shared:CRUProgramDetails.max_price_error_message',
              {
                value: getPriceString({
                  currency: currency,
                  price: MAX_PRICE,
                }),
              },
            );
            isValid = false;
          }

          field.isValid = isValid;

          return isValid;
        }),
        defaultValue: '0',
        validationRule: {
          optional: true,
          type: 'string',
        },
      }),
    }).current;

    useEffect(() => {
      if (program) {
        const lastEdition = program.editions[program.editions.length - 1];

        if (lastEdition) {
          groupSize.current = lastEdition.group_size || groupSize.current;

          runInAction(() => {
            fields.groupSize.value = `${
              lastEdition.group_size || groupSize.current
            }`;
            fields.groupSize.validationRule!.optional = groupSize.current === 1;
          });

          setType(
            groupSize.current > 1
              ? ProgramTypeEnum.GROUP
              : ProgramTypeEnum.INDIVIDUAL,
          );

          setDuration(lastEdition.duration || duration);

          setIsEvergreen(isEditionEvergreen(lastEdition) || isEvergreen);

          setPaymentOption(
            lastEdition.payment_plan && lastEdition.payment_plan.total
              ? ProgramPaymentOptionEnum.CUSTOM
              : paymentOption,
          );

          setPaymentProcessing(
            lastEdition.payment_plan
              ? lastEdition.payment_plan.processing
              : paymentProcessing,
          );

          if (!internalProcessingIsAvailable) {
            onPaymentProcessingChange(true);
          }

          setPaymentPlan(lastEdition.payment_plan || paymentPlan);

          price.current = lastEdition.payment_plan
            ? lastEdition.payment_plan.total
            : price.current;

          setCurrency(
            lastEdition.payment_plan
              ? Currency[lastEdition.payment_plan.currency]
              : currency,
          );

          const priceString = `${price.current}`;

          runInAction(() => {
            fields.price.value =
              price.current > 0
                ? `${priceString.slice(0, -2)}.${priceString.slice(-2)}`
                : '';
          });

          runInAction(() => {
            fields.clientConferencesCount.validationRule!.optional =
              groupSize.current > 1;

            fields.clientConferencesCount.value =
              lastEdition.client_conferences.count.toString();

            setClientConferenceDuration(
              lastEdition.client_conferences.duration ||
                clientConferenceDuration,
            );
          });

          if (lastEdition.contract) {
            if (
              (lastEdition.contract as FileApiModel)._id ||
              lastEdition.contract instanceof File
            ) {
              setContract(lastEdition.contract || contract);
            }
          }

          setPrivateDataRequest(lastEdition.private_data_request);
        }
      }

      fetchInvitesCount();

      return () => {
        fetchInvitesCountDelay.current.cancel();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getData = useCallback(
      async (direction?: 'back' | 'next') => {
        if (validateFields(fields)) {
          if (
            direction === 'next' &&
            paymentPlan.total &&
            paymentPlan.processing === 'internal' &&
            !currentUserStore.user!.is_provider
          ) {
            const isConfirmed = await connectStripeConfirm();
            const stripeIsSetup = isConfirmed
              ? await connectStripe({fromScreen: 'create_program_screen'})
              : false;

            if (!stripeIsSetup) {
              return;
            }
          }

          let lastEdition: null | ProgramT['editions'][0] =
            program && program.editions.length
              ? program.editions[program.editions.length - 1]!
              : null;

          lastEdition = {
            ...lastEdition,
            client_conferences: {
              count:
                type.id === ProgramTypeEnum.INDIVIDUAL.id
                  ? parseInt(fields.clientConferencesCount.value, 10)
                  : 0,
              duration:
                type.id === ProgramTypeEnum.INDIVIDUAL.id
                  ? clientConferenceDuration
                  : 0,
              min_pause: 0,
            },
            contract: contract || null,
            duration: isEvergreen ? EVERGREEN_PROGRAM_DURATION : duration,
            group_size:
              type.id === ProgramTypeEnum.INDIVIDUAL.id ? 1 : groupSize.current,
            is_autorestarting: isEvergreen,
            payment_plan: paymentPlan.total ? paymentPlan : null,
            private_data_request: privateDataRequest.slice(),
          } as ProgramT['editions'][0];

          let editions: ProgramT['editions'];

          if (program && program.editions.length) {
            program.editions.splice(
              program.editions.length - 1,
              1,
              lastEdition,
            );

            editions = program.editions;
          } else {
            editions = [lastEdition];
          }

          return {
            editions,
          };
        }
      },
      [
        clientConferenceDuration,
        contract,
        duration,
        fields,
        paymentPlan,
        privateDataRequest,
        program,
        type.id,
        isEvergreen,
      ],
    );

    const onProgramTypeChange = useCallback(
      (programType: ProgramType) => {
        runInAction(() => {
          fields.groupSize.validationRule!.optional =
            programType.id === ProgramTypeEnum.INDIVIDUAL.id;
          fields.clientConferencesCount.validationRule!.optional =
            programType.id === ProgramTypeEnum.GROUP.id;
        });

        if (
          programType.id === ProgramTypeEnum.GROUP.id &&
          groupSize.current === 1
        ) {
          groupSize.current = DEFAULT_GROUP_SIZE;
          fields.groupSize.setValue(`${DEFAULT_GROUP_SIZE}`);
        }

        setIsEvergreen(false);
        setType(programType);

        logger.event(
          programType.id === ProgramTypeEnum.GROUP.id
            ? 'group_program_type_tap'
            : 'individual_program_type_tap',
        );

        emitter.emit(PROGRAM_TYPE_CHANGE_EVENT, programType);
      },
      [fields.clientConferencesCount.validationRule, fields.groupSize],
    );

    const onGroupSizeChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        let gropeSizeText = e.currentTarget.value.replace(/\D/g, '');

        if (gropeSizeText === '0') {
          gropeSizeText = '';
        }

        groupSize.current = gropeSizeText ? parseInt(gropeSizeText, 10) : 0;

        fields.groupSize.setValue(gropeSizeText);

        fields.groupSize.validate();
      },
      [fields.groupSize],
    );

    const onDurationButtonClick = useCallback(() => {
      showDurationModal();
    }, [showDurationModal]);

    const onDurationSet = useCallback(
      (newDuration: number) => {
        setDuration(newDuration);

        setPaymentPlan(
          createPaymentPlan({
            currency: parsedPaymentPlan.parsedCurrency,
            duration: newDuration,
            paymentType:
              parsedPaymentPlan.paymentType.id ===
                ProgramPaymentTypeEnum.MONTHLY.id &&
              isMonthlyPaymentAvailable(parsedPaymentPlan.total, newDuration)
                ? ProgramPaymentTypeEnum.MONTHLY
                : ProgramPaymentTypeEnum.ONE_TIME,
            price: parsedPaymentPlan.total,
            processing: paymentProcessing,
          }),
        );

        hideDurationModal();
      },
      [
        hideDurationModal,
        parsedPaymentPlan.parsedCurrency,
        parsedPaymentPlan.paymentType.id,
        parsedPaymentPlan.total,
        paymentProcessing,
      ],
    );

    const onPaymentOptionChange = useCallback(
      (option: ProgramPaymentOption) => {
        fields.price.validationRule!.optional =
          option.id !== ProgramPaymentOptionEnum.CUSTOM.id;

        setPaymentPlan(
          option.id !== ProgramPaymentOptionEnum.CUSTOM.id
            ? DefaultPaymentPlan
            : createPaymentPlan({
                currency: parsedPaymentPlan.parsedCurrency,
                duration,
                paymentType:
                  parsedPaymentPlan.paymentType.id ===
                    ProgramPaymentTypeEnum.MONTHLY.id &&
                  isMonthlyPaymentAvailable(parsedPaymentPlan.total, duration)
                    ? ProgramPaymentTypeEnum.MONTHLY
                    : ProgramPaymentTypeEnum.ONE_TIME,
                price: price.current,
                processing: paymentProcessing,
              }),
        );

        setPaymentOption(option);
      },
      [
        fields.price.validationRule,
        parsedPaymentPlan.total,
        parsedPaymentPlan.paymentType.id,
        parsedPaymentPlan.parsedCurrency,
        duration,
        paymentProcessing,
      ],
    );

    const onPriceTextChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        let priceText = e.currentTarget.value
          .replace(/[^\d., d]/g, '')
          .replace(currency.sign, '');

        if (priceText === '.' || priceText === ',') {
          priceText = '';
        } else if (priceText === '0' || priceText === '') {
          priceText = '';
        } else if (priceText.includes('.') || priceText.includes(',')) {
          let point = '.';

          if (priceText.includes(',')) {
            point = ',';
          }

          const tmp = priceText.split(point);

          priceText = `${tmp[0]}${point}${tmp[1]!.slice(0, 2)}`;
        }

        price.current = priceText
          ? (parseFloat(priceText.replace(',', '.')) * 1000) / 10
          : 0;

        setPaymentPlan(
          createPaymentPlan({
            currency,
            duration,
            paymentType: parsedPaymentPlan.paymentType,
            price: price.current,
            processing: paymentProcessing,
          }),
        );

        fields.price.setValue(priceText);

        fields.price.validate();
      },
      [
        currency,
        duration,
        fields.price,
        parsedPaymentPlan.paymentType,
        paymentProcessing,
      ],
    );

    const onClientConferencesCountTextChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const countText = e.currentTarget.value.replace(/\D/g, '');

        fields.clientConferencesCount.setValue(countText);

        fields.clientConferencesCount.validate();
      },
      [fields.clientConferencesCount],
    );

    const onClientConferenceDurationChange = useCallback((value: number) => {
      setClientConferenceDuration(value);
    }, []);

    const onPaymentPlanButtonClick = useCallback(() => {
      showPaymentPlanModal();
    }, [showPaymentPlanModal]);

    const onPaymentPlanChange = useCallback(
      (newPaymentPlan: PaymentPlan) => {
        setPaymentPlan(newPaymentPlan);

        hidePaymentPlanModal();
      },
      [hidePaymentPlanModal],
    );

    const onPaymentProcessingChange = useCallback(
      (value: boolean) => {
        setPaymentProcessing(value ? 'external' : 'internal');

        setPaymentPlan(
          createPaymentPlan({
            currency,
            duration,
            paymentType: parsedPaymentPlan.paymentType,
            price: price.current,
            processing: value ? 'external' : 'internal',
          }),
        );
      },
      [currency, duration, parsedPaymentPlan.paymentType, price],
    );

    const onContractButtonClick = useCallback(
      (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        e.preventDefault();

        contextMenu.show({
          event: e,
          id: CONTRACT_CONTEXT_MENU_ID,
        });
      },
      [],
    );

    const onViewContractButtonClick = useCallback(() => {
      if (contract && (contract as FileApiModel)._id) {
        NiceModal.show(DocumentViewerModal, {
          document: contract as FileApiModel,
          title: t('shared:CRUProgramDetails.contract_viewer_title'),
        });
      }
    }, [contract, t]);

    const onChooseContractButtonClick = useCallback(() => {
      showAddContractModal();
    }, [showAddContractModal]);

    const onChooseContractFromLibraryButtonClick = useCallback(() => {
      showAddContractFromLibraryModal();
    }, [showAddContractFromLibraryModal]);

    const onRemoveContractButtonClick = useCallback(() => {
      setContract(undefined);
    }, []);

    const renderContractContextMenu = useCallback(
      () => (
        <ContextMenu.Menu animation="fade" id={CONTRACT_CONTEXT_MENU_ID}>
          {contract ? (
            <ContextMenu.Item onClick={onRemoveContractButtonClick}>
              {t('shared:CRUProgramDetails.remove_contract_button')}
            </ContextMenu.Item>
          ) : null}
          {contract && (contract as FileApiModel)._id ? (
            <ContextMenu.Item onClick={onViewContractButtonClick}>
              {t('shared:CRUProgramDetails.view_contract_button')}
            </ContextMenu.Item>
          ) : null}
          <ContextMenu.Item onClick={onChooseContractButtonClick}>
            {t('shared:CRUProgramDetails.choose_contract_button')}
          </ContextMenu.Item>
          <ContextMenu.Item onClick={onChooseContractFromLibraryButtonClick}>
            {t(
              'shared:CRUProgramDetails.choose_contract_from_yc_library_button',
            )}
          </ContextMenu.Item>
        </ContextMenu.Menu>
      ),
      [
        contract,
        onChooseContractButtonClick,
        onChooseContractFromLibraryButtonClick,
        onRemoveContractButtonClick,
        onViewContractButtonClick,
        t,
      ],
    );

    const onContractSelect = useCallback(
      (files: File[]) => {
        const file = files[0]!;

        hideAddContractModal();

        setContract(file);

        fileStore.upload(file, {
          uploadingKey: PROGRAM_CONTRACT_UPLOADING_KEY,
        });
      },
      [hideAddContractModal],
    );

    const checkFileType = useCallback(
      (name: string, mime: string) => {
        const allowedFileType = SUPPORTED_FORMATS.document.mimetypes;

        const ext = getFileExtension(name || 'Unknown').toLowerCase();

        if (!allowedFileType.includes(mime || '')) {
          showAlert(
            t('shared:ext_files_not_supported', {
              ext,
            }),
          );

          return false;
        }

        return true;
      },
      [t],
    );

    // TODO: Library refactoring
    const onChooseContractFromLibrary = useCallback(
      (listSelect: TListDataItem[]) => {
        hideAddContractFromLibraryModal();

        const file = (listSelect[0] as ILink).resource;

        if (!file) {
          return;
        }

        if (checkFileType(getFileName(file), file.src.original.mimetype)) {
          setContract(file);
        }
      },
      [checkFileType, hideAddContractFromLibraryModal],
    );

    const onPrivateDataRequestButtonClick = useCallback(() => {
      showPrivateDataRequestModal();
    }, [showPrivateDataRequestModal]);

    const onPrivateDataRequestChange = useCallback(
      (newPrivateDataRequest: EditionApiModel['private_data_request']) => {
        setPrivateDataRequest(newPrivateDataRequest);

        hidePrivateDataRequestModal();
      },
      [hidePrivateDataRequestModal],
    );

    const fetchInvitesCount = useCallback(async () => {
      if (!program || !program._id) {
        return;
      }

      try {
        const result = await apiRequest({
          method: 'coach.invites.count',
          params: {
            query: [['program_id', '==', program._id]],
          },
        });

        setSentInvitesCount(result._count);
      } catch (error) {
        logger.error(error);

        fetchInvitesCountDelay.current = delay(2000);

        await fetchInvitesCountDelay.current;

        fetchInvitesCount();
      }
    }, [program]);

    return (
      <div>
        <div className={commonStyles.dataRow}>
          <h3 className={commonStyles.header}>
            {t('shared:CRUProgramDetails.type_label')}
            {':'}
            {shouldCloneProgram || !userIsOwner ? (
              <span className={commonStyles.sub}>{type.toString()}</span>
            ) : null}
          </h3>
          {!shouldCloneProgram && userIsOwner ? (
            <div className={styles.typeOptionsContainer}>
              {[ProgramTypeEnum.GROUP, ProgramTypeEnum.INDIVIDUAL].map(
                programType => {
                  const isSelected = programType.id === type.id;

                  return (
                    <div
                      className={classNames(
                        styles.typeOption,
                        isSelected && styles.selected,
                      )}
                      data-test-id={
                        TestID.create_program_details.block.program_type[
                          programType.id
                        ]
                      }
                      key={programType.id}
                      onClick={() => onProgramTypeChange(programType)}
                    >
                      <Image
                        className={styles.typeOptionIcon}
                        src={
                          programType.id === ProgramTypeEnum.GROUP.id
                            ? ProgramGroupIcon
                            : ProgramIndividualIcon
                        }
                      />
                      <p className={styles.typeOptionText}>
                        {isSelected ? (
                          <CheckIcon className={styles.typeOptionCheckIcon} />
                        ) : null}
                        {programType.toString()}
                      </p>
                    </div>
                  );
                },
              )}
            </div>
          ) : (
            <>
              <div className={commonStyles.message}>
                {t('shared:CRUProgramDetails.edit_type_label')}
              </div>
              <Button
                className={commonStyles.cloneButton}
                onClick={cloneProgram}
              >
                <CloneProgramIcon />
                {t('shared:CRUProgramDetails.clone_program_button')}
              </Button>
            </>
          )}
        </div>
        {type.id === ProgramTypeEnum.GROUP.id ? (
          <div className={commonStyles.dataRow}>
            <h3 className={commonStyles.header}>
              {t('shared:CRUProgramDetails.group_size_label')}
              {':'}
              {!userIsOwner ? (
                <span className={commonStyles.sub}>
                  {fields.groupSize.value}
                </span>
              ) : null}
            </h3>
            {!userIsOwner ? (
              <div className={commonStyles.message}>
                {t('shared:CRUProgramDetails.edit_group_size_label')}
              </div>
            ) : (
              <Observer>
                {() => (
                  <TextField
                    data-test-id={TestID.create_program_details.input.max_size}
                    disabled={!userIsOwner}
                    error={fields.groupSize.error || ''}
                    onChange={onGroupSizeChange}
                    placeholder={t(
                      'shared:CRUProgramDetails.group_size_text_input_placeholder',
                      {
                        max_group_size: MAX_GROUP_SIZE,
                        min_group_size: MIN_GROUP_SIZE,
                      },
                    )}
                    ref={fields.groupSize.ref}
                    value={fields.groupSize.value}
                  />
                )}
              </Observer>
            )}
          </div>
        ) : null}
        {!isOfferedProgram && type.id === ProgramTypeEnum.INDIVIDUAL.id ? (
          <div className={commonStyles.dataRow}>
            <h3 className={commonStyles.header}>
              {t('shared:CRUProgramDetails.evergreen_label')}
              {':'}
              {!userIsOwner || shouldCloneProgram ? (
                <span className={commonStyles.sub}>
                  {
                    /**
                     * @i18n_parser
                     *
                     * t('shared:CRUProgramDetails.evergreen_yes_label')
                     * t('shared:CRUProgramDetails.evergreen_no_label')
                     */
                    t(
                      `shared:CRUProgramDetails.evergreen_${isEvergreen ? 'yes' : 'no'}_label`,
                    )
                  }
                </span>
              ) : null}
              {isEvergreenEditable && userIsOwner ? (
                <Toggle isActive={isEvergreen} onClick={setIsEvergreen} />
              ) : null}
            </h3>
            {!userIsOwner || shouldCloneProgram ? (
              <>
                <div className={commonStyles.message}>
                  {t('shared:CRUProgramDetails.edit_evergreen_label')}
                </div>
                <Button
                  className={commonStyles.cloneButton}
                  onClick={cloneProgram}
                >
                  <CloneProgramIcon />
                  {t('shared:CRUProgramDetails.clone_program_button')}
                </Button>
              </>
            ) : (
              <Button
                className={classNames({
                  [styles.dataRowButton!]: true,
                  [styles.sub!]: true,
                })}
                style={{cursor: 'initial'}}
              >
                {t('shared:CRUProgramDetails.evergreen_description')}
              </Button>
            )}
          </div>
        ) : null}
        {!isEvergreen ? (
          <div className={commonStyles.dataRow}>
            <h3 className={commonStyles.header}>
              {t('shared:CRUProgramDetails.duration_label')}
              {':'}
              {!isDurationEditable || !userIsOwner ? (
                <span className={commonStyles.sub}>
                  {parsedDuration.toString()}
                </span>
              ) : null}
            </h3>
            {isDurationEditable && userIsOwner ? (
              <>
                <Button
                  className={styles.dataRowButton}
                  data-test-id={
                    TestID.create_program_details.button.duration.open
                  }
                  onClick={onDurationButtonClick}
                >
                  {parsedDuration.toString()}
                  <ArrowNextIcon />
                </Button>
                <DurationModal
                  duration={duration}
                  isOpen={durationModalIsOpen}
                  onAfterClose={hideDurationModal}
                  onSet={onDurationSet}
                />
              </>
            ) : (
              <>
                <div className={commonStyles.message}>
                  {t('shared:CRUProgramDetails.edit_duration_label')}
                </div>
                <Button
                  className={commonStyles.cloneButton}
                  onClick={cloneProgram}
                >
                  <CloneProgramIcon />
                  {t('shared:CRUProgramDetails.clone_program_button')}
                </Button>
              </>
            )}
          </div>
        ) : null}
        {!isOfferedProgram ? (
          <>
            <div
              className={classNames(
                commonStyles.dataRow,
                !userIsOwner && styles.customDataRow,
              )}
            >
              <h3 className={commonStyles.header}>
                {t('shared:CRUProgramDetails.payment_label')}
                {':'}
                {!userIsOwner ? (
                  <span className={commonStyles.sub}>
                    {parsedPaymentPlan.toString()}
                  </span>
                ) : null}
              </h3>
              {!userIsOwner ? (
                <div className={commonStyles.message}>
                  {t('shared:CRUProgramDetails.edit_payment_label')}
                </div>
              ) : (
                <div className={styles.paymentOptionsContainer}>
                  {[
                    ProgramPaymentOptionEnum.FREE,
                    ProgramPaymentOptionEnum.CUSTOM,
                  ].map(option => {
                    const isSelected = option.id === paymentOption.id;

                    return (
                      <div
                        className={classNames(
                          styles.paymentOption,
                          isSelected && styles.selected,
                        )}
                        data-test-id={`${TestID.create_program_details.block.payment_option}.${option.id}`}
                        key={option.id}
                        onClick={() => onPaymentOptionChange(option)}
                      >
                        <p className={styles.paymentOptionText}>
                          {option.toString()}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {paymentOption.id === ProgramPaymentOptionEnum.CUSTOM.id ? (
              <>
                {/* TODO: Enable currency picker when we'll have the support of currencies */}
                {/* <div className={commonStyles.dataRow}>
              <h3 className={commonStyles.header}>
                {t(shared:CRUProgramDetails.currency_label')}
                {':'}
                {!userIsOwner ? (
                  <span className={commonStyles.sub}>
                    {currency.toString()}
                  </span>
                ) : null}
              </h3>
              {userIsOwner ? (
                <>
                  <Button
                    className={styles.dataRowButton}
                    onClick={onCurrencyButtonClick}>
                    {currency.toString()}
                    <ArrowNextIcon />
                  </Button>
                </>
              ) : (
                <>
                  <div className={commonStyles.message}>
                    {t('shared:CRUProgramDetails.edit_currency_label')}
                  </div>
                </>
              )}
            </div> */}
                <div className={commonStyles.dataRow}>
                  {userIsOwner ? (
                    <>
                      <div className={styles.priceTextFieldContainer}>
                        <Observer>
                          {() => (
                            <TextField
                              disabled={!userIsOwner}
                              error={fields.price.error || ''}
                              onChange={onPriceTextChange}
                              placeholder={t(
                                'shared:CRUProgramDetails.price_text_input_placeholder',
                                {
                                  max_price: getPriceString({
                                    currency,
                                    price: MAX_PRICE,
                                  }),
                                  min_price: getPriceString({
                                    currency,
                                    price: MIN_PRICE,
                                  }),
                                },
                              )}
                              ref={fields.groupSize.ref}
                              value={
                                !fields.price.value ||
                                fields.price.value === '0'
                                  ? ''
                                  : `${currency.sign}${fields.price.value}`
                              }
                            />
                          )}
                        </Observer>
                        {price.current > 0 &&
                        !fields.price.error &&
                        monthlyPaymentIsAvailableMemo ? (
                          <>
                            <Button
                              className={styles.paymentPlanButton}
                              onClick={onPaymentPlanButtonClick}
                            >
                              {t(
                                'shared:CRUProgramDetails.payment_plan_button',
                              )}
                            </Button>
                            <PaymentPlanModal
                              duration={duration}
                              isOpen={paymentPlanModalIsOpen}
                              onAfterClose={hidePaymentPlanModal}
                              onSet={onPaymentPlanChange}
                              paymentPlan={paymentPlan}
                            />
                          </>
                        ) : null}
                      </div>
                      <div className={commonStyles.message}>
                        {price.current > 0 && !fields.price.error
                          ? paymentProcessing === 'internal'
                            ? t(
                                'shared:CRUProgramDetails.price_description_with_price_and_commission_label',
                                {
                                  commission: `${getPriceString({
                                    price: commission,
                                  })}${
                                    parsedPaymentPlan.paymentType.id ===
                                    ProgramPaymentTypeEnum.MONTHLY.id
                                      ? getMonthlyPaymentPerUnitString().toLowerCase()
                                      : ''
                                  }`,
                                  price: parsedPaymentPlan.toString(),
                                },
                              )
                            : t(
                                'shared:CRUProgramDetails.price_description_with_price_label',
                                {
                                  price: parsedPaymentPlan.toString(),
                                },
                              )
                          : t(
                              'shared:CRUProgramDetails.price_description_label',
                            )}
                      </div>
                    </>
                  ) : null}
                  <div className={styles.paymentProcessingContainer}>
                    <div className={styles.paymentProcessingContent}>
                      <p className={styles.paymentProcessingTitle}>
                        {t('shared:CRUProgramDetails.paid_elsewhere.title')}
                      </p>
                      <p className={styles.paymentProcessingDescription}>
                        {t(
                          'shared:CRUProgramDetails.paid_elsewhere.description',
                        )}
                      </p>
                    </div>
                    <Switch
                      disabled={!userIsOwner || !internalProcessingIsAvailable}
                      isOn={paymentProcessing === 'external'}
                      onChange={onPaymentProcessingChange}
                    />
                  </div>
                  {!internalProcessingIsAvailable ? (
                    <div className={commonStyles.message}>
                      {t(
                        'shared:CRUProgramDetails.edit_payment_processing_isnt_provider_coaches_label',
                      )}
                    </div>
                  ) : null}
                  {!userIsOwner ? (
                    <>
                      <div className={commonStyles.message}>
                        {t(
                          'shared:CRUProgramDetails.edit_payment_processing_label',
                        )}
                      </div>
                      <Button
                        className={commonStyles.cloneButton}
                        onClick={cloneProgram}
                      >
                        <CloneProgramIcon />
                        {t('shared:CRUProgramDetails.clone_program_button')}
                      </Button>
                    </>
                  ) : null}
                </div>
              </>
            ) : null}
          </>
        ) : null}
        {type.id === ProgramTypeEnum.INDIVIDUAL.id ? (
          <>
            <h3 className={commonStyles.header}>
              {
                /**
                 * @i18n_parser
                 *
                 * t('shared:CRUProgramDetails.client_conferences_count_label.evergreen')
                 * t('shared:CRUProgramDetails.client_conferences_count_label.regular')
                 */
                t(
                  `shared:CRUProgramDetails.client_conferences_count_label.${isEvergreen ? 'evergreen' : 'regular'}`,
                )
              }
              {':'}
              {!userIsOwner || isOfferedProgram ? (
                <span className={commonStyles.sub}>
                  {fields.clientConferencesCount.value}
                </span>
              ) : null}
            </h3>
            {userIsOwner && !isOfferedProgram ? (
              <div className={commonStyles.dataRow}>
                <Observer>
                  {() => (
                    <TextField
                      data-test-id={
                        TestID.create_program_details.input.number_of_sessions
                      }
                      error={fields.clientConferencesCount.error || ''}
                      maxLength={3}
                      onChange={onClientConferencesCountTextChange}
                      placeholder={t(
                        'shared:CRUProgramDetails.client_conferences_count_text_input_placeholder',
                      )}
                      ref={fields.clientConferencesCount.ref}
                      value={fields.clientConferencesCount.value}
                    />
                  )}
                </Observer>
              </div>
            ) : !isOfferedProgram ? (
              <div className={commonStyles.message}>
                {t(
                  'shared:CRUProgramDetails.edit_client_conferences_count_label',
                )}
              </div>
            ) : null}
            <div className={commonStyles.dataRow}>
              <h3 className={commonStyles.header}>
                {t('shared:CRUProgramDetails.client_conference_duration_label')}
                {':'}
                {!userIsOwner || isOfferedProgram ? (
                  <span className={commonStyles.sub}>
                    {t('shared:CRUProgramDetails.client_conference_duration', {
                      duration: clientConferenceDuration,
                    })}
                  </span>
                ) : null}
              </h3>
              {userIsOwner && !isOfferedProgram ? (
                <div className={styles.clientConferenceDurationContainer}>
                  {CLIENT_CONFERENCE_DURATIONS.map(item => {
                    const isSelected = item === clientConferenceDuration;

                    return (
                      <div
                        className={classNames(
                          styles.clientConferenceDuration,
                          isSelected && styles.selected,
                        )}
                        data-test-id={
                          TestID.create_program_details.button
                            .individual_conference_duration
                        }
                        key={item}
                        onClick={() => onClientConferenceDurationChange(item)}
                      >
                        <p className={styles.clientConferenceDurationText}>
                          {t(
                            'shared:CRUProgramDetails.client_conference_duration',
                            {
                              duration: item,
                            },
                          )}
                        </p>
                      </div>
                    );
                  })}
                </div>
              ) : !isOfferedProgram ? (
                <div className={commonStyles.message}>
                  {t(
                    'shared:CRUProgramDetails.edit_client_conferences_duration_label',
                  )}
                </div>
              ) : null}
            </div>
          </>
        ) : null}
        {!isOfferedProgram ? (
          <>
            <div className={commonStyles.dataRow}>
              <h3 className={commonStyles.header}>
                {t('shared:CRUProgramDetails.contract_label')}
                {':'}
              </h3>
              <div
                onClick={
                  userIsOwner
                    ? onContractButtonClick
                    : contract
                      ? onViewContractButtonClick
                      : undefined
                }
              >
                {(!userIsOwner && contract) || userIsOwner ? (
                  <div className={styles.contractContainer}>
                    <div
                      className={classNames(
                        styles.contractPreview,
                        contract && styles.attached,
                      )}
                    >
                      <DocumentIcon />
                    </div>
                    {contract ? (
                      <div>
                        <p className={styles.contractName}>
                          {contract instanceof File
                            ? contract.name
                            : getFileName(contract)}
                        </p>
                        <p className={styles.contractSize}>
                          {i18n.fileSize(
                            contract instanceof File
                              ? contract.size
                              : getFileSrc(contract).size,
                          )}
                        </p>
                      </div>
                    ) : (
                      <p className={styles.attachContractText}>
                        {t('shared:CRUProgramDetails.attach_contract_button')}
                      </p>
                    )}
                  </div>
                ) : null}
              </div>
              {renderContractContextMenu()}
              <AddFileModal
                fileType="document"
                isOpen={addContractModalIsOpen}
                mode="select"
                onAfterClose={hideAddContractModal}
                onSelect={onContractSelect}
              />
              <ModalAnimateWin
                className="greyHeaderContainer List"
                classNameBody="whiteBody maxContent"
                classNameCloseBut="greyHeaderBut"
                classNameHeader="greyHeader"
                closeModalHandler={hideAddContractFromLibraryModal}
                header={'Select document'}
                isBody
                showModal={addContractFromLibraryModalIsOpen}
              >
                <LibraryState>
                  <AddFile getListItems={onChooseContractFromLibrary} />
                </LibraryState>
              </ModalAnimateWin>
              {!userIsOwner ? (
                <div className={commonStyles.message}>
                  {t('shared:CRUProgramDetails.edit_contract_label')}
                </div>
              ) : null}
            </div>
            <div className={commonStyles.dataRow}>
              <h3 className={commonStyles.header}>
                {t('shared:CRUProgramDetails.personal_info_label')}
                {':'}
              </h3>
              <div
                className={classNames(
                  styles.personalInfoContainer,
                  !userIsOwner && styles.disabled,
                )}
                onClick={
                  userIsOwner ? onPrivateDataRequestButtonClick : undefined
                }
              >
                <div className={styles.personalInfoListContainer}>
                  {privateDataRequest.length ? (
                    <div className={styles.personalInfoList}>
                      {privateDataRequest.map(key => (
                        <div className={styles.personalInfoItem} key={key}>
                          {
                            /**
                             * @i18n_parser
                             *
                             * t('shared:private_profile.first_name')
                             * t('shared:private_profile.middle_name')
                             * t('shared:private_profile.last_name')
                             * t('shared:private_profile.email')
                             * t('shared:private_profile.phone')
                             * t('shared:private_profile.gender')
                             * t('shared:private_profile.date_of_birth')
                             * t('shared:private_profile.location')
                             */
                            t(`shared:private_profile.${key}`)
                          }
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={styles.selectPersonalInfo}>
                      {t('shared:CRUProgramDetails.personal_info_none_label')}
                    </div>
                  )}
                </div>
                {userIsOwner ? <ArrowNextIcon /> : null}
              </div>
              <PrivateDataRequestModal
                isOpen={privateDataRequestModalIsOpen}
                onAfterClose={hidePrivateDataRequestModal}
                onSet={onPrivateDataRequestChange}
                privateDataRequest={privateDataRequest}
              />
              {!userIsOwner ? (
                <div className={commonStyles.message}>
                  {t('shared:CRUProgramDetails.edit_personal_info_label')}
                </div>
              ) : null}
            </div>
          </>
        ) : null}
      </div>
    );
  },
);

export default memo(CRUProgramDetails, isEqual);
